import {
  useCallback, useMemo, useState,
} from 'react';
import { useDispatch } from 'react-redux';
import {
  MapPrivacyLevel, type MapSource,
} from '~/_shared/types/map';
import {
  isNullsy, notNullsy,
} from '~/_shared/utils/typeGuards';
import { type MapInfoInputField } from '~/map/mapInfoInputField.enum';
import { mapInfoUpdatePrivacy } from '~/store/mapInfo/mapInfo.actionCreators';
import { useClientIdSelector } from '~/store/selectors/useClientIdSelector';
import { useMapIdSelector } from '~/store/selectors/useMapIdSelector';
import { PasswordOption } from '../mapPrivacySelector/mapPrivacySelector.types';
import {
  type MapPrivacyFieldErrors, useUpdateMapPrivacy,
} from '../mapPrivacySelectorModal/useUpdateMapPrivacy.hook';
import { useMapPrivacyRestrictions } from './useMapPrivacyRestrictions.hook';

export type ManageMapPrivacyState = {
  disabledPrivacyLevels: ReadonlyArray<MapPrivacyLevel>;
  isPasswordProtected: boolean;
  isFormValid: boolean;
  isLoading: boolean;
};

export type ManageMapPrivacyErrors = {
  fieldErrors: MapPrivacyFieldErrors;
  globalError?: string;
  privacyLevel: MapPrivacyLevel;
};

export type ManageMapPrivacyCallbacks = {
  clearGlobalError?: () => void;
  clearFieldError: (field: MapInfoInputField) => void;
  onPasswordOptionChange: (option: PasswordOption) => void;
  onPrivacyLevelChange: (privacyLevel: MapPrivacyLevel, password?: string) => void;
  onSave: () => void;
  setIsFormValid: (isValid: boolean) => void;
};

export type MapPrivacyInfo = {
  mapPrivacyLevel: MapPrivacyLevel;
  mapId: number;
  parentMapId?: number;
  parentMapPrivacy?: MapPrivacyLevel;
  mapSource: MapSource;
};

type ManageMapPrivacyProps = {
  isSnapshot: boolean;
  mapPrivacyInfo?: MapPrivacyInfo;

  onSuccess: (newPrivacyLevel: MapPrivacyLevel) => void;
};

export const useManageMapPrivacy = ({
  mapPrivacyInfo, onSuccess, isSnapshot,
}: ManageMapPrivacyProps) => {
  const clientId = useClientIdSelector();

  const [isFormValid, setIsFormValid] = useState(false);
  const [keepExistingPassword, setKeepExistingPassword] = useState(
    mapPrivacyInfo?.mapPrivacyLevel === MapPrivacyLevel.PasswordProtected
  );
  const [password, setPassword] = useState('');
  const [privacyLevel, setPrivacyLevel] = useState<MapPrivacyLevel>(
    mapPrivacyInfo?.mapPrivacyLevel || MapPrivacyLevel.Private
  );

  const isPasswordProtected = mapPrivacyInfo?.mapPrivacyLevel === MapPrivacyLevel.PasswordProtected;

  const dispatch = useDispatch();
  const currentMapId = useMapIdSelector();
  const isCurrentMap = currentMapId === mapPrivacyInfo?.mapId;

  const {
    isLoading, fieldErrors, globalError,
    clearGlobalError, clearFieldError,
    updateMapPrivacy, updateSnapshotPrivacy,
  } = useUpdateMapPrivacy();

  const snapshotPrivacyRestrictions = useMapPrivacyRestrictions({
    currentMapPrivacyLevel: privacyLevel,
    isSnapshot,
    mapSource: mapPrivacyInfo?.mapSource,
    parentMapPrivacyLevel: mapPrivacyInfo?.parentMapPrivacy,
  });

  const onPasswordOptionChange = useCallback((option: PasswordOption) => {
    setKeepExistingPassword(option === PasswordOption.preserve);
    clearGlobalError();
  }, [clearGlobalError]);

  const onSave = useCallback(() => {
    if (keepExistingPassword) {
      onSuccess(MapPrivacyLevel.PasswordProtected);
      return;
    }

    const passwordProtectedWithoutPassword = (privacyLevel === MapPrivacyLevel.PasswordProtected) && !password;
    if (isNullsy(clientId) || isNullsy(mapPrivacyInfo?.mapId) || !privacyLevel || passwordProtectedWithoutPassword) {
      return;
    }

    if (isSnapshot && mapPrivacyInfo?.parentMapPrivacy && notNullsy(mapPrivacyInfo?.parentMapId)) {
      updateSnapshotPrivacy({
        mapId: mapPrivacyInfo.parentMapId,
        clientId,
        snapshotId: mapPrivacyInfo.mapId,
        privacyLevel: privacyLevel || MapPrivacyLevel.Public,
        password: password ?? null,
        password_confirmation: password ?? null,
      })
        .then(() => {
          clearGlobalError();
          if (isCurrentMap) {
            dispatch(mapInfoUpdatePrivacy(privacyLevel));
          }
          onSuccess(privacyLevel);
        });
    }
    else {
      updateMapPrivacy({
        mapId: mapPrivacyInfo.mapId,
        clientId,
        privacyLevel: privacyLevel || MapPrivacyLevel.Public,
        password: password ?? null,
        password_confirmation: password ?? null,
      })
        .then(() => {
          clearGlobalError();
          if (isCurrentMap) {
            dispatch(mapInfoUpdatePrivacy(privacyLevel));
          }
          onSuccess(privacyLevel);
        });
    }
  }, [clearGlobalError, clientId, dispatch, isCurrentMap, isSnapshot, keepExistingPassword, mapPrivacyInfo?.mapId,
    mapPrivacyInfo?.parentMapId, mapPrivacyInfo?.parentMapPrivacy, onSuccess, password, privacyLevel,
    updateMapPrivacy, updateSnapshotPrivacy]);

  const onPrivacyLevelChange = useCallback((privacyLevel: MapPrivacyLevel, password?: string) => {
    setPassword(password || '');
    setPrivacyLevel(privacyLevel);
    setKeepExistingPassword(false);
    clearGlobalError();
  }, [clearGlobalError]);

  return useMemo(() => ({
    clearFieldError,
    clearGlobalError: globalError ? clearGlobalError : undefined,
    disabledPrivacyLevels: snapshotPrivacyRestrictions.disabledPrivacyLevels,
    fieldErrors,
    globalError: globalError || snapshotPrivacyRestrictions.warning,
    isPasswordProtected,
    isFormValid: (isFormValid || keepExistingPassword) && !fieldErrors.size && !globalError && snapshotPrivacyRestrictions.isValid && !!mapPrivacyInfo,
    isLoading,
    onPasswordOptionChange,
    onPrivacyLevelChange,
    onSave,
    privacyLevel,
    setIsFormValid,
  }), [clearFieldError, clearGlobalError, fieldErrors, globalError, isPasswordProtected, isFormValid,
    isLoading, keepExistingPassword, onPasswordOptionChange, onPrivacyLevelChange, onSave, privacyLevel,
    snapshotPrivacyRestrictions, mapPrivacyInfo,
  ]);
};
